import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import ActionIcon from "../../../src/assets/icons/ActionIcon.svg";
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import apiService, { parseSupportTicketsListResponse } from '../../services/api';
import { DATE_FORMATS } from '../../constants';
import {
  Paper,
  Chip,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { usePagination } from '../../hooks/usePagination';
import Pagination from '../../components/ui/Pagination';
import SortableTable, { SortableColumn } from '../../components/common/SortableTable';
import useCodeCommon from '../../hooks/useCodeCommon';
import useSupportUsers from '../../hooks/useSupportUsers';
import { RootState } from '../../store';
import SearchableSelectInput from '../../components/forms/SearchableSelectInput';
import CustomLoading from '../../components/common/CustomLoading';
import useRBAC from '../../hooks/useRBAC';
import { formatMobileNumber } from '../../utils/formatters';

/** Calendar date for API / URL state — no UTC shift (what the user picked in the picker). */
const toCreatedDateParam = (d: Dayjs | null): string | null =>
  d ? d.startOf('day').format('YYYY-MM-DD') : null;

function parseTicketCategoryId(
  category: string,
  options: { codevalue: string; codedisplayname: string }[]
): number | null {
  if (!category) return null;
  const raw = options.find((o) => o.codedisplayname === category)?.codevalue;
  if (raw == null || String(raw).trim() === "") return null;
  const v = Number.parseInt(String(raw), 10);
  return Number.isFinite(v) ? v : null;
}

interface SupportTicket {
  id: string;
  ticketId: string;
  userName: string;
  mobileNo: string;
  createdAt: string;
  hubName: string;
  category: string;
  priority: string;
  assignee: string;
  status: string;
  hubmasterid?: string | number;
}

const StatusChip = ({ status }: { status: string }) => {
  const getChipStyles = (status: string) => {
    switch (status) {
      case "Open":
        return {
          backgroundColor: "rgba(227, 242, 253, 1)",
          color: "rgba(25, 118, 210, 1)",
          fontWeight: 600,
        };
      case "InProgress":
        return {
          backgroundColor: "rgba(255, 248, 220, 1)",
          color: "rgba(184, 134, 11, 1)",
          fontWeight: 600,
        };
      case "Resolved":
      case "Closed":
      case "Refunded":
        return {
          backgroundColor: "rgba(212, 255, 199, 1)",
          color: "rgba(31, 113, 13, 1)",
          fontWeight: 600,
        };
      case "Awaiting User":
      case "Pending":
        return {
          backgroundColor: "rgba(255, 245, 157, 1)",
          color: "rgba(107, 105, 14, 1)",
          fontWeight: 600,
        };
      case "Failed":
        return {
          backgroundColor: "rgba(255, 227, 227, 1)",
          color: "rgba(197, 0, 0, 1)",
          fontWeight: 600,
        };
      default:
        return {
          backgroundColor: "rgba(245, 245, 245, 1)",
          color: "rgba(117, 117, 117, 1)",
          fontWeight: 600,
        };
    }
  };

  return <Chip label={status} size="small" sx={getChipStyles(status)} />;
};


const SupportPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const { isSupport } = useRBAC();
  /** `useRBAC()` returns new function refs each render; using a ref avoids unstable `fetchSupportTickets` + fetch loops. */
  const isSupportRef = useRef(false);
  isSupportRef.current = isSupport();

  const initialState = location.state || {};
  const initialPage = initialState.page || 0;
  
  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm || '');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
    initialState.selectedDate != null && initialState.selectedDate !== ''
      ? dayjs(initialState.selectedDate)
      : null
  );
  const [assignee, setAssignee] = useState(initialState.assignee || '');
  const [priority, setPriority] = useState(initialState.priority || '');
  const [status, setStatus] = useState(initialState.status || '');
  const [category, setCategory] = useState(initialState.category || '');
  const [supportTickets, setSupportTickets] = useState<SupportTicket[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const {
    options: allSupportStatusOptions,
    loading: supportStatusLoading,
    error: supportStatusError,
  } = useCodeCommon("SupportTicketStatus");
  
  const {
    options: allCategoryOptions,
    loading: categoryLoading,
  } = useCodeCommon("TicketCategory");
  
  const {
    options: allPriorityOptions,
    loading: priorityLoading,
  } = useCodeCommon("TicketPriority");
  
  const {
    users: assignableUsers,
    loading: usersLoading,
  } = useSupportUsers(4);
  
  const getCategoryDisplayName = useCallback((categoryid: number): string => {
    const categoryOption = allCategoryOptions.find(
      (option) => Number.parseInt(option.codevalue) === categoryid
    );
    return categoryOption ? categoryOption.codedisplayname : '';
  }, [allCategoryOptions]);

  const getPriorityDisplayName = useCallback((priorityid: number): string => {
    const priorityOption = allPriorityOptions.find(
      (option) => Number.parseInt(option.codevalue) === priorityid
    );
    return priorityOption ? priorityOption.codedisplayname : '';
  }, [allPriorityOptions]);

  const getAssigneeDisplayName = useCallback((assigneeId: number): string => {
    const assigneeUser = assignableUsers.find(
      (user) => user.userid === assigneeId
    );
    return assigneeUser ? assigneeUser.name : '';
  }, [assignableUsers]);

  const categoryOptionsList = React.useMemo(
    () =>
      allCategoryOptions
        .map((o) => o.codedisplayname)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [allCategoryOptions]
  );
  const priorityOptionsList = React.useMemo(
    () =>
      allPriorityOptions
        .map((o) => o.codedisplayname)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [allPriorityOptions]
  );
  const statusOptionsList = React.useMemo(
    () =>
      allSupportStatusOptions
        .map((o) => o.codedisplayname)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [allSupportStatusOptions]
  );
  const assigneeOptionsList = React.useMemo(
    () =>
      assignableUsers
        .map((u) => u.name)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [assignableUsers]
  );

  const pagination = usePagination({
    totalItems: totalCount,
    initialPage,
    initialRowsPerPage: 10,
  });

  const handleCategoryChange = (value: string) => {
    pagination.goToPage(0);
    setCategory(value);
    navigate(location.pathname, {
      replace: true,
      state: { ...(location.state as object), category: value, page: 0 },
    });
  };

  const handlePriorityChange = (value: string) => {
    pagination.goToPage(0);
    setPriority(value);
    navigate(location.pathname, {
      replace: true,
      state: { ...(location.state as object), priority: value, page: 0 },
    });
  };

  const handleStatusChange = (value: string) => {
    pagination.goToPage(0);
    setStatus(value);
    navigate(location.pathname, {
      replace: true,
      state: { ...(location.state as object), status: value, page: 0 },
    });
  };

  const handleAssigneeChange = (value: string) => {
    pagination.goToPage(0);
    setAssignee(value);
    navigate(location.pathname, {
      replace: true,
      state: { ...(location.state as object), assignee: value, page: 0 },
    });
  };

  const columns: SortableColumn<SupportTicket>[] = [
    { id: 'ticketId', label: 'Ticket ID' },
    { id: 'userName', label: 'User Name' },
    { id: 'mobileNo', label: 'Mobile No.' },
    { id: 'createdAt', label: 'Created At' },
    { id: 'hubName', label: 'Hub Name' },
    { id: 'category', label: 'Category' },
    { id: 'priority', label: 'Priority' },
    { id: 'assignee', label: 'Assignee' },
    { 
      id: 'status', 
      label: 'Status', 
      align: 'center',
      render: (row: SupportTicket) => <StatusChip status={row.status} />
    },
    {
      id: 'actions' as keyof SupportTicket,
      label: 'Action',
      align: 'center',
      sortable: false,
      render: (row: SupportTicket) => (
        <Button 
          variant="text" 
          sx={{ p: 0, minWidth: "auto" }}
          onClick={() => handleViewDetails(row.id)}
        >
          <img src={ActionIcon} alt="Action" width={30} height={30}/>
        </Button>
      )
    },
  ];

  const getSupportStatusDisplayName = useCallback((statusid: number): string => {
    const statusOption = allSupportStatusOptions.find(
      (option) => Number.parseInt(option.codevalue) === statusid
    );
    return statusOption ? statusOption.codedisplayname : '';
  }, [allSupportStatusOptions]);

  const fetchSupportTickets = useCallback(async () => {
    if (!user?.userid) {
      setError("User not authenticated. Please login again.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const ticketcategoryid = parseTicketCategoryId(category, allCategoryOptions);
      const ticketpriorityid = !priority
        ? null
        : Number.parseInt(
            allPriorityOptions.find((o) => o.codedisplayname === priority)?.codevalue ?? "0",
            10
          ) || null;
      const statusid = !status
        ? 0
        : Number.parseInt(
            allSupportStatusOptions.find((o) => o.codedisplayname === status)?.codevalue ?? "0",
            10
          ) || 0;
      const assigneeForApi =
        isSupportRef.current && user.userid
          ? user.userid
          : !assignee
            ? 0
            : assignableUsers.find((u) => u.name === assignee)?.userid ?? 0;
      const createdDateParam = toCreatedDateParam(selectedDate);

      const response = await apiService.searchSupportTickets({
        userId: user.userid,
        pageNumber: pagination.page + 1,
        pageSize: pagination.rowsPerPage,
        ticketid: searchTerm.trim(),
        createdDate: createdDateParam,
        assignee: assigneeForApi,
        ticketpriorityid,
        statusid,
        ticketcategoryid,
      });

      const { items, total } = parseSupportTicketsListResponse(response);
      const list = items as Record<string, unknown>[];

      const transformedTickets: SupportTicket[] = list.map((ticket: Record<string, unknown>, index: number) => {
        const statusDisplayName = getSupportStatusDisplayName(Number(ticket.statusid) || 0);
        const categoryDisplayName = getCategoryDisplayName(Number(ticket.ticketcategoryid) || 0);
        const priorityDisplayName = getPriorityDisplayName(Number(ticket.ticketpriorityid) || 0);
        const assigneeDisplayName = getAssigneeDisplayName(Number(ticket.assignee) || 0);
        const hubRaw = ticket.hubmasterid ?? ticket.hubid;
        const hubmasterid =
          hubRaw === undefined || hubRaw === null
            ? undefined
            : typeof hubRaw === "string" || typeof hubRaw === "number"
              ? hubRaw
              : String(hubRaw);

        return {
          id:
            String(ticket.ticketmasterid ?? ticket.id ?? index),
          ticketId: String(ticket.ticketid ?? ""),
          userName: String(ticket.username ?? ""),
          mobileNo: formatMobileNumber(ticket.phoneno as string | undefined) || "",
          createdAt: DATE_FORMATS.formatDateTime(String(ticket.enteredon ?? "")),
          hubName: String(ticket.hubname ?? "N/A"),
          category: categoryDisplayName,
          priority: priorityDisplayName,
          assignee: assigneeDisplayName,
          status: statusDisplayName,
          hubmasterid,
        };
      });

      setSupportTickets(transformedTickets);
      setTotalCount(total);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to fetch support tickets";
      setError(message);
      setSupportTickets([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    user?.userid,
    pagination.page,
    pagination.rowsPerPage,
    searchTerm,
    selectedDate,
    assignee,
    priority,
    status,
    category,
    allSupportStatusOptions,
    allCategoryOptions,
    allPriorityOptions,
    assignableUsers,
    getSupportStatusDisplayName,
    getCategoryDisplayName,
    getPriorityDisplayName,
    getAssigneeDisplayName,
  ]);

  useEffect(() => {
    if (!user?.userid) {
      setLoading(false);
      return;
    }
    if (
      supportStatusLoading ||
      categoryLoading ||
      priorityLoading ||
      usersLoading
    ) {
      return;
    }
    if (
      allSupportStatusOptions.length === 0 ||
      allCategoryOptions.length === 0 ||
      allPriorityOptions.length === 0
    ) {
      return;
    }
    void fetchSupportTickets();
  }, [
    fetchSupportTickets,
    user?.userid,
    supportStatusLoading,
    categoryLoading,
    priorityLoading,
    usersLoading,
    allSupportStatusOptions.length,
    allCategoryOptions.length,
    allPriorityOptions.length,
  ]);

  useEffect(() => {
    if (!user?.userid) return;
    const ticketcategoryid = parseTicketCategoryId(category, allCategoryOptions);
    const ticketpriorityid = !priority
      ? null
      : Number.parseInt(
          allPriorityOptions.find((o) => o.codedisplayname === priority)?.codevalue ?? "0",
          10
        ) || null;
    const statusid = !status
      ? 0
      : Number.parseInt(
          allSupportStatusOptions.find((o) => o.codedisplayname === status)?.codevalue ?? "0",
          10
        ) || 0;
    const assigneeForExport =
      isSupportRef.current && user.userid
        ? user.userid
        : !assignee
          ? 0
          : assignableUsers.find((u) => u.name === assignee)?.userid ?? 0;
    const createdDateParam = toCreatedDateParam(selectedDate);
    try {
      localStorage.setItem(
        "supportExportFilters",
        JSON.stringify({
          userId: user.userid,
          pageNumber: 1,
          pageSize: 10000,
          ticketid: searchTerm.trim(),
          createdDate: createdDateParam,
          assignee: assigneeForExport,
          ticketpriorityid,
          statusid,
          ticketcategoryid,
        })
      );
    } catch {
      /* ignore */
    }
  }, [
    user?.userid,
    searchTerm,
    selectedDate,
    assignee,
    priority,
    status,
    category,
    allCategoryOptions,
    allPriorityOptions,
    allSupportStatusOptions,
    assignableUsers,
  ]);

  useEffect(() => {
    const state = location.state as { refresh?: boolean; refreshHubs?: boolean } | null;
    if (state?.refresh || state?.refreshHubs) {
      void fetchSupportTickets();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, fetchSupportTickets, location.pathname]);

  const handleViewDetails = (supportId: string) => {
    const selected = supportTickets.find((ticket) => ticket.id === supportId);
    if (!selected) return;

    navigate(
      `/support/details?id=${encodeURIComponent(supportId)}&userName=${encodeURIComponent(selected.userName || "")}`,
      {
        state: {
          searchTerm,
          selectedDate: toCreatedDateParam(selectedDate),
          assignee,
          priority,
          status,
          category,
          page: pagination.page,
        },
      }
    );
  };

   
  return (
    <>
    <Container fluid className="px-0">
      {(error || supportStatusError) && (
        <Alert 
          severity="error" 
          className="mb-3" 
          onClose={() => setError(null)}
        >
          {error || supportStatusError}
        </Alert>
      )}

      <Paper className="mb-3 p-3 border-0 rounded-0 shadow-none">
        <Row className="g-3 align-items-end">
          <Col xs={12} md={2}>
            <TextField
              label="Search"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => {
                const newSearchTerm = e.target.value;
                setSearchTerm(newSearchTerm);
                pagination.goToPage(0);
                navigate(location.pathname, {
                  replace: true,
                  state: {
                    ...(location.state as object),
                    searchTerm: newSearchTerm,
                    page: 0,
                  }
                });
              }}
            />
          </Col>

          <Col xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date"
                value={selectedDate}
                onChange={(newValue) => {
                  setSelectedDate(newValue);
                  pagination.goToPage(0);
                  navigate(location.pathname, {
                    replace: true,
                    state: {
                      ...(location.state as object),
                      selectedDate: toCreatedDateParam(newValue),
                      page: 0,
                    }
                  });
                }}
                slotProps={{
                  field: { clearable: true, onClear: () => {
                    setSelectedDate(null);
                    pagination.goToPage(0);
                    navigate(location.pathname, {
                      replace: true,
                      state: {
                        ...(location.state as object),
                        selectedDate: null,
                        page: 0,
                      }
                    });
                  }},
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Col>

          <Col xs={12} md={2}>
            <SearchableSelectInput
              field="assignee"
              formData={{ assignee }}
              onChange={(_, value) => handleAssigneeChange(value)}
              options={assigneeOptionsList}
              label="Assignee"
              placeholder="Search and select assignee"
              loading={usersLoading}
              disabled={usersLoading}
            />
          </Col>

          <Col xs={12} md={2}>
            <SearchableSelectInput
              field="priority"
              formData={{ priority }}
              onChange={(_, value) => handlePriorityChange(value)}
              options={priorityOptionsList}
              label="Priority"
              placeholder="Search and select priority"
              loading={priorityLoading}
              disabled={priorityLoading}
            />
          </Col>

          <Col xs={12} md={2}>
            <SearchableSelectInput
              field="status"
              formData={{ status }}
              onChange={(_, value) => handleStatusChange(value)}
              options={statusOptionsList}
              label="Status"
              placeholder="Search and select status"
              loading={supportStatusLoading}
              disabled={supportStatusLoading}
            />
          </Col>

          <Col xs={12} md={2}>
            <SearchableSelectInput
              field="category"
              formData={{ category }}
              onChange={(_, value) => handleCategoryChange(value)}
              options={categoryOptionsList}
              label="Category"
              placeholder="Search and select category"
              loading={categoryLoading}
              disabled={categoryLoading}
            />
          </Col>
        </Row>
      </Paper>

      <Paper className="border-0 rounded-0 shadow-none">
        {loading ? (
          <CustomLoading message="Fetching Support Table Data..." />
        ) : (
          <SortableTable
            data={supportTickets}
            columns={columns}
            emptyMessage="No support tickets found"
          />
        )}
      </Paper>
    </Container>
    <Pagination
      page={pagination.page}
      rowsPerPage={pagination.rowsPerPage}
      totalItems={totalCount}
      totalPages={pagination.totalPages}
      startItem={pagination.startItem}
      endItem={pagination.endItem}
      rowsPerPageOptions={[5, 10, 25, 50]}
      showRowsPerPage
       onRowsPerPageChange={pagination.handleChangeRowsPerPage}
       onGoToNextPage={pagination.goToNextPage}
       onGoToPreviousPage={pagination.goToPreviousPage}
    />
  </>
  );
};

export default SupportPage;
